import { useCallback, useState } from 'react';

/** Overflow of a pixel or less is rounding, not anything to scroll to. */
const ROUNDING_PX = 1;

/**
 * Whether an element's content is taller than its box, so it scrolls: read as it mounts, and again
 * whenever it or one of its children changes size. The element alone would miss it: held at a floor
 * or a cap, its box keeps its size while what is inside it grows or shrinks, which its children's
 * boxes show (`useHorizontalScrollFade` observes its children for the same reason).
 *
 * Returns the ref callback for the element, and the answer. Without a `ResizeObserver` it is read
 * once, as it mounts.
 */
export function useCanScrollY(): [
  ref: (node: HTMLElement | null) => (() => void) | undefined,
  canScroll: boolean,
] {
  const [canScroll, setCanScroll] = useState(false);
  // Stable, so React calls it only as the element mounts and leaves, never on a re-render.
  const ref = useCallback((node: HTMLElement | null) => {
    if (node === null) return undefined;
    const measure = () => {
      setCanScroll(node.scrollHeight - node.clientHeight > ROUNDING_PX);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    for (const child of Array.from(node.children)) observer.observe(child);
    return () => {
      observer.disconnect();
    };
  }, []);
  return [ref, canScroll];
}
